/**
 * Фронтенд-сервис интеграции с локальным кассовым агентом (ФЗ-54).
 *
 * Подключается по WebSocket к Hardware Agent (ws://127.0.0.1:8089),
 * отправляет чек на фискализацию по протоколу АТОЛ JSON и получает
 * фискальные реквизиты (ФД/ФПД/ФН). Авто-реконнект с задержкой.
 */

import type { CorrectionInfo, ReceiptDocType, SettlementType } from '@core/db/models'
import { generateUUID } from '../../utils/crypto'
import {
  isNativeKkt,
  nativeKktCloseShift,
  nativeKktOpenShift,
  nativeKktPrintReceipt,
  nativeKktPrintXReport,
  nativeKktReadFnDocuments,
  nativeKktSelfTest,
  nativeKktStatus,
  nativeKktTestPrint,
  type NativeKktFiscalData,
  type NativeKktMarkingLicense,
  type NativeKktPrintResult,
  type NativeKktReadDocumentsResult,
  type NativeKktStatus
} from '@core/native/kkt'
import { buildAtolReceiptTask } from './atolTask'
import { checkMarkingReadiness } from './preflight'
import { debugLog } from '@core/utils/log'

/** Тип фискального документа в терминах агента (ФФД). */
export type FiscalDocType =
  | 'sell'
  | 'sell_return'
  | 'buy'
  | 'buy_return'
  | 'sell_correction'
  | 'sell_return_correction'
  | 'buy_correction'
  | 'buy_return_correction'

/** Маппинг «наш признак расчёта → базовый тип документа ФФД». */
const SETTLEMENT_TO_DOC: Record<
  SettlementType,
  'sell' | 'sell_return' | 'buy' | 'buy_return'
> = {
  income: 'sell',
  income_return: 'sell_return',
  outcome: 'buy',
  outcome_return: 'buy_return'
}

/** Итоговый тип документа: базовый + суффикс `_correction` для коррекции. */
export function resolveFiscalDocType(
  docType: ReceiptDocType = 'sale',
  settlementType: SettlementType = 'income'
): FiscalDocType {
  const base = SETTLEMENT_TO_DOC[settlementType] ?? 'sell'
  return (docType === 'correction' ? `${base}_correction` : base) as FiscalDocType
}

export interface FiscalDocResult {
  fiscalDocumentNumber: number
  fiscalDocumentSign: string
  shiftNumber: number
  fiscalDriveNumber?: string
  receiptNumber?: number
  dateTime?: string
  /** Документ фискализирован, но физически не напечатан (нет бумаги и т.п.). */
  notPrinted?: boolean
}

/** Данные чека, достаточные для фискализации. */
export interface FiscalReceiptInput {
  id?: string
  cashierName?: string
  total_amount: number
  /** Способ оплаты: 'cash' — наличные (тег 1031), иначе безнал (тег 1081). */
  paymentMethod?: string
  items: Array<{
    name: string
    price: number
    quantity: number
    unit?: string
    marking_code?: string
  }>
  /** Тип документа: обычная продажа или чек коррекции. */
  docType?: ReceiptDocType
  /** Признак расчёта для коррекции (ФФД 1054). */
  settlementType?: SettlementType
  /** Реквизиты коррекции (основание/предписание). */
  correction?: CorrectionInfo
}

interface PendingRequest {
  resolve: (val: FiscalDocResult) => void
  reject: (err: Error) => void
}

/** Итоговое состояние ККТ (приоритет: no_device → error → paper_out → shift_closed → ok). */
export type KktState = 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device'

/** Ошибка ККТ с расшифровкой от драйвера. */
export interface KktErrorInfo {
  code: string
  text: string
  fatal?: boolean
}

/** Состояние фискального накопителя. */
export interface KktFnStatus {
  number?: string
  validTo?: string
  daysLeft?: number
  state?: 'ready' | 'needs_replacement' | 'exhausted' | 'archived' | 'error'
}

/** Состояние обмена с ОФД (очередь внутри ФН — только чтение). */
export interface KktOfdStatus {
  unsentDocs?: number
  lastSentAt?: string
  lastError?: string
}

/** Полный статус ККТ (событие KKT_STATUS от Hardware Agent). */
export interface KktStatus {
  connected: boolean
  model?: string
  serial?: string
  firmware?: string
  interface?: 'usb' | 'com' | 'tcp' | 'emulator'
  rnm?: string
  inn?: string
  sno?: string
  shiftOpen?: boolean
  /** Номер открытой смены ККТ (если драйвер его отдаёт). */
  shiftNumber?: number
  /** Версия ФФД, например '1.2'. */
  ffdVersion?: string
  /** Подтверждена ли лицензия ЕСМ/ЛМ ЧЗ. */
  esmEnabled?: boolean
  /** Поддерживает ли ККТ тег 2000 (imcParams). */
  markingSupport?: boolean
  /** Детали лицензии «Обработка маркировки» из native-драйвера. */
  markingLicense?: NativeKktMarkingLicense
  paper?: 'ok' | 'low' | 'out' | 'unknown'
  coverOpen?: boolean
  /** Номер последнего документа (ФД) в ККТ. */
  lastDocNumber?: number
  /** Количество чеков в открытой смене (если отдаёт драйвер). */
  receiptNumber?: number
  clock?: string
  fn?: KktFnStatus
  ofd?: KktOfdStatus
  /** Последняя ошибка/предупреждение устройства для быстрого показа. */
  lastError?: string
  errors: KktErrorInfo[]
  state: KktState
}

export type SelfTestStatus = 'ok' | 'warn' | 'fail' | 'skip'

/** Пункт пошаговой самопроверки ККТ. */
export interface SelfTestCheck {
  id: string
  label: string
  status: SelfTestStatus
  detail?: string
}

export class FiscalService {
  private ws: WebSocket | null = null
  private isConnected = false
  /** true — Android-путь через Capacitor KktPlugin, false — WebSocket-агент. */
  private native = false
  private nativeStatusTimer: number | null = null
  private nativeConnectedNotified = false
  private readonly pendingRequests = new Map<string, PendingRequest>()
  /** Хуки, вызываемые при УСПЕШНОМ подключении к агенту (напр. дофискализация). */
  private readonly connectHooks: Array<() => void> = []
  /** Последний полученный статус ККТ (кэш для UI). */
  private kktStatus: KktStatus | null = null
  /** Подписчики на push-события KKT_STATUS. */
  private readonly kktListeners = new Set<(status: KktStatus) => void>()
  /** Ожидающие ответы на самопроверку/тест-печать (по requestId). */
  private readonly pendingSelfTests = new Map<
    string,
    { resolve: (checks: SelfTestCheck[]) => void; reject: (err: Error) => void }
  >()
  private readonly pendingTestPrints = new Map<
    string,
    {
      resolve: (result: { success: boolean; error?: string }) => void
      reject: (err: Error) => void
    }
  >()

  /**
   * Подписаться на подключение к кассовому агенту. Позволяет, например,
   * FiscalQueueWorker'у сразу разгрести очередь отложенных чеков, как только
   * ККТ вернулась в строй (без ожидания следующего интервала).
   */
  public onConnect(cb: () => void): void {
    this.connectHooks.push(cb)
  }

  public connect(url = 'ws://127.0.0.1:8089'): void {
    if (isNativeKkt()) {
      this.connectNative()
      return
    }

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    try {
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.isConnected = true
        debugLog('[FiscalService] Connected to Local Hardware Agent')
        // Сразу спрашиваем статус ККТ (агент мог подняться раньше кассы).
        this.requestStatus()
        // Уведомляем подписчиков (дофискализация отложенных чеков).
        for (const hook of this.connectHooks) {
          try {
            hook()
          } catch (err) {
            console.warn('[FiscalService] onConnect hook failed:', err)
          }
        }
      }

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(String(event.data))
          if (msg.event === 'PRINT_RESULT' && msg.requestId) {
            const promise = this.pendingRequests.get(msg.requestId)
            if (promise) {
              if (msg.success) {
                promise.resolve(msg.fiscalData as FiscalDocResult)
              } else {
                promise.reject(new Error(msg.error || 'Ошибка печати фискального чека'))
              }
              this.pendingRequests.delete(msg.requestId)
            }
          } else if (msg.event === 'KKT_STATUS') {
            const status = msg as KktStatus
            this.kktStatus = status
            for (const listener of this.kktListeners) {
              try {
                listener(status)
              } catch (err) {
                console.warn('[FiscalService] KKT listener failed:', err)
              }
            }
          } else if (msg.event === 'KKT_TEST_RESULT' && msg.requestId) {
            const pending = this.pendingSelfTests.get(msg.requestId)
            if (pending) {
              this.pendingSelfTests.delete(msg.requestId)
              pending.resolve((msg.checks ?? []) as SelfTestCheck[])
            }
          } else if (msg.event === 'KKT_TEST_PRINT_RESULT' && msg.requestId) {
            const pending = this.pendingTestPrints.get(msg.requestId)
            if (pending) {
              this.pendingTestPrints.delete(msg.requestId)
              pending.resolve({ success: Boolean(msg.success), error: msg.error })
            }
          }
        } catch (err) {
          console.error('[FiscalService] Failed to parse agent message:', err)
        }
      }

      this.ws.onclose = () => {
        this.isConnected = false
        // Авто-реконнект через 5 секунд.
        setTimeout(() => this.connect(url), 5000)
      }

      this.ws.onerror = () => {
        this.isConnected = false
      }
    } catch {
      this.isConnected = false
    }
  }

  /** Android: KKT подключается не по WebSocket, а через native KktPlugin. */
  private connectNative(): void {
    if (this.native || this.nativeStatusTimer != null) return
    this.native = true
    // До первого успешного status() ККТ ещё не подтверждена. Иначе SaleService
    // считает её доступной и пытается печатать вслепую.
    this.isConnected = false
    void this.refreshNativeStatus()
    this.nativeStatusTimer = window.setInterval(() => {
      void this.refreshNativeStatus()
    }, 5000)
  }

  private async refreshNativeStatus(): Promise<void> {
    try {
      const status = this.normalizeNativeStatus(await nativeKktStatus())
      this.kktStatus = status
      this.isConnected = status.connected
      for (const listener of this.kktListeners) {
        try {
          listener(status)
        } catch (error) {
          console.warn('[FiscalService] native KKT listener failed:', error)
        }
      }

      if (status.state === 'ok' && !this.nativeConnectedNotified) {
        this.nativeConnectedNotified = true
        for (const hook of this.connectHooks) {
          try {
            hook()
          } catch (error) {
            console.warn('[FiscalService] native onConnect hook failed:', error)
          }
        }
      } else if (status.state !== 'ok') {
        this.nativeConnectedNotified = false
      }
    } catch (error) {
      this.isConnected = false
      console.warn('[FiscalService] native KKT status failed:', error)
    }
  }

  private normalizeNativeStatus(status: NativeKktStatus): KktStatus {
    return {
      ...status,
      interface: (status.interface as KktStatus['interface']) ?? 'usb',
      errors: Array.isArray(status.errors) ? status.errors : [],
      fn: status.fn
        ? {
            number: status.fn.number,
            validTo: status.fn.validTo,
            daysLeft: status.fn.daysLeft,
            state: status.fn.state as KktFnStatus['state']
          }
        : undefined,
      ofd: status.ofd
        ? {
            unsentDocs: status.ofd.unsentDocs,
            lastSentAt: status.ofd.lastSentAt,
            lastError: status.ofd.lastError
          }
        : undefined
    }
  }

  public async printFiscalReceipt(receiptData: FiscalReceiptInput): Promise<FiscalDocResult> {
    if (this.native) {
      return this.printNativeReceipt(receiptData)
    }

    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Кассовый агент не подключен. Проверьте локальный сервис печати.')
    }

    const requestId = generateUUID()
    // 54-ФЗ: наличные → тег 1031 (cash), безнал (карта/СБП/QR) → тег 1081.
    const isCash = receiptData.paymentMethod === 'cash' || receiptData.paymentMethod == null
    const payload = {
      command: 'PRINT_RECEIPT',
      requestId,
      payload: {
        type: resolveFiscalDocType(receiptData.docType, receiptData.settlementType),
        uuid: receiptData.id || requestId,
        electronically: false,
        operator: { name: receiptData.cashierName || 'Кассир' },
        items: receiptData.items.map((i) => ({
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          amount: i.price * i.quantity,
          // Без НДС по умолчанию (УСН/Патент).
          tax: { type: 'none' },
          markingCode: i.marking_code ? { type: 'raw', mark: i.marking_code } : undefined,
        })),
        payments: [
          { type: isCash ? 'cash' : 'electronically', sum: receiptData.total_amount },
        ],
        total: receiptData.total_amount,
        // Реквизиты коррекции (основание/предписание) — только для *_correction.
        correction: receiptData.correction,
      },
    }

    return new Promise<FiscalDocResult>((resolve, reject) => {
      // Таймаут на печать 10 секунд.
      const timeout = setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId)
          reject(new Error('Превышено время ожидания ответа от ККТ'))
        }
      }, 10000)

      this.pendingRequests.set(requestId, {
        resolve: (data) => {
          clearTimeout(timeout)
          resolve(data)
        },
        reject: (err) => {
          clearTimeout(timeout)
          reject(err)
        },
      })

      this.ws!.send(JSON.stringify(payload))
    })
  }

  private async printNativeReceipt(receiptData: FiscalReceiptInput): Promise<FiscalDocResult> {
    const uuid = receiptData.id || generateUUID()
    const task = buildAtolReceiptTask(receiptData)
    const result: NativeKktPrintResult = await nativeKktPrintReceipt(
      JSON.stringify(task),
      uuid
    )
    if (!result.success) {
      throw new Error(result.error || 'ККТ не пробила фискальный чек')
    }
    if (!result.fiscalData) {
      throw new Error('ККТ не вернула фискальные реквизиты')
    }
    return this.mapNativeFiscalData(result.fiscalData, Boolean(result.notPrinted))
  }

  private mapNativeFiscalData(
    data: NativeKktFiscalData,
    notPrinted = false
  ): FiscalDocResult {
    return {
      fiscalDocumentNumber: Number(data.fiscalDocumentNumber),
      fiscalDocumentSign: String(data.fiscalDocumentSign),
      shiftNumber: Number(data.shiftNumber),
      fiscalDriveNumber: data.fiscalDriveNumber,
      receiptNumber: data.receiptNumber == null ? undefined : Number(data.receiptNumber),
      dateTime: data.dateTime,
      notPrinted
    }
  }

  /**
   * Подписка на статус ККТ (push от агента). Возвращает функцию отписки.
   * Сразу отдаёт закэшированный статус, если он уже получен.
   */
  public onKktStatus(cb: (status: KktStatus) => void): () => void {
    this.kktListeners.add(cb)
    if (this.kktStatus) cb(this.kktStatus)
    return () => this.kktListeners.delete(cb)
  }

  /** Последний известный статус ККТ (null — ещё не получен). */
  public getKktStatus(): KktStatus | null {
    return this.kktStatus
  }

  /**
   * Готовность к маркировке ФФД 1.2. Fail-closed: пока статус не подтвердил
   * ФФД >= 1.2, ЕСМ и поддержку тега 2000, маркированную продажу не пускаем.
   */
  public getMarkingReadiness(): { ok: boolean; reason?: string } {
    return checkMarkingReadiness(this.kktStatus)
  }

  /** Запросить актуальный статус ККТ (KKT_GET_STATUS). */
  public requestStatus(): void {
    if (this.native) {
      void this.refreshNativeStatus()
      return
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ command: 'KKT_GET_STATUS' }))
    }
  }

  /** Пошаговая самопроверка ККТ (KKT_SELF_TEST). */
  public async runSelfTest(): Promise<SelfTestCheck[]> {
    if (this.native) {
      const result = await nativeKktSelfTest()
      return result.checks ?? []
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Агент оборудования не подключён')
    }
    const requestId = generateUUID()
    return new Promise<SelfTestCheck[]>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingSelfTests.delete(requestId)
        reject(new Error('Превышено время ожидания самопроверки ККТ'))
      }, 15000)
      this.pendingSelfTests.set(requestId, {
        resolve: (checks) => {
          clearTimeout(timeout)
          resolve(checks)
        },
        reject: (err) => {
          clearTimeout(timeout)
          reject(err)
        }
      })
      this.ws!.send(JSON.stringify({ command: 'KKT_SELF_TEST', requestId }))
    })
  }

  /** Нефискальная тест-печать чека (KKT_TEST_PRINT). */
  public async testPrint(): Promise<{ success: boolean; error?: string }> {
    if (this.native) {
      return nativeKktTestPrint()
    }
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return { success: false, error: 'Агент оборудования не подключён' }
    }
    const requestId = generateUUID()
    return new Promise<{ success: boolean; error?: string }>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingTestPrints.delete(requestId)
        reject(new Error('Превышено время ожидания тест-печати'))
      }, 15000)
      this.pendingTestPrints.set(requestId, {
        resolve: (result) => {
          clearTimeout(timeout)
          resolve(result)
        },
        reject: (err) => {
          clearTimeout(timeout)
          reject(err)
        }
      })
      this.ws!.send(JSON.stringify({ command: 'KKT_TEST_PRINT', requestId }))
    })
  }

  public getStatus(): { isConnected: boolean } {
    return { isConnected: this.isConnected }
  }

  /** Обновить статус ККТ вручную (после openShift/closeShift). */
  public async refreshKktStatus(): Promise<void> {
    if (this.native) {
      await this.refreshNativeStatus();
    }
  }

  /** Read-only чтение документов ФН для сверки открытой смены (Android native). */
  public async readKktDocuments(
    from: number,
    to: number
  ): Promise<NativeKktReadDocumentsResult> {
    if (this.native) return nativeKktReadFnDocuments(from, to);
    return { success: false, error: 'kkt_documents_not_supported_on_desktop' };
  }

  /** Нефискальный X-отчёт. Не закрывает смену и не меняет Z. */
  public async printXReport(): Promise<{ success: boolean; error?: string }> {
    if (this.native) {
      try {
        const result = await nativeKktPrintXReport()
        await this.refreshNativeStatus()
        return result
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        return { success: false, error: message }
      }
    }
    return { success: false, error: 'kkt_x_report_not_supported' }
  }

  /** Открыть смену в ККТ. Native — дожидаемся ответа и обновляем статус. */
  public async openShiftOnKkt(
    shiftNumber?: number
  ): Promise<{ success: boolean; error?: string }> {
    if (this.native) {
      try {
        const result = await nativeKktOpenShift();
        await this.refreshNativeStatus();
        return result;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.warn('[FiscalService] native openShift failed:', message);
        return { success: false, error: message };
      }
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ command: 'OPEN_SHIFT', requestId: generateUUID(), shiftNumber }));
    }
    return { success: true };
  }

  /** Закрыть смену в ККТ. Native — дожидаемся ответа и обновляем статус. */
  public async closeShiftOnKkt(): Promise<{
    success: boolean
    zReportNumber?: number
    error?: string
  }> {
    if (this.native) {
      try {
        const result = await nativeKktCloseShift()
        await this.refreshNativeStatus()
        return result
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        console.warn('[FiscalService] native closeShift failed:', message)
        return { success: false, error: message }
      }
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ command: 'CLOSE_SHIFT', requestId: generateUUID() }))
      return { success: true }
    }
    return { success: false, error: 'kkt_not_connected' }
  }
}

export const fiscalService = new FiscalService()
